import { action } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { AppManager, Variables } from 'core';
import { analytics } from 'core/analytics';
import { WizardStore } from 'libs/wizards/app';

import { ProfileModel, ProfileApp, ProfilesApp } from 'apps/profile/app';
import { UserApp } from 'apps/user/app';
import { InitiativeApp } from 'apps/initiative/app';
import { clientStore } from 'apps/client/app';
import { raiseUserError } from 'apps/utils';
import { SignUpOrganizationMutation } from 'apps/profile/gql';
import { conf } from 'apps/profile/config.yaml';
import { conf as globalConf } from 'apps/config.yaml';
import { TagsApp } from 'apps/tag/app';

import { i18n } from 'config/i18n';

const { siteName } = globalConf;

export class SignUpOrganizationAppManager extends AppManager {
  static registryName = 'SignUpOrganizationAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('profile', ProfileApp);
    this.AppRef('initiative', InitiativeApp);
    this.AppRef('invites', ProfilesApp);

    this.profile = new ProfileApp(this);
    this.profile.user = new UserApp(this);
    this.initiative = new InitiativeApp(this);
    this.invites = new ProfilesApp(this);
    this.tags = new TagsApp(this);
    this.wizard = new WizardStore(this, conf, 'signUpOrganizationWizard', {
      makeItDouble: this.MakeItDouble,
    });
    this.wizard.SetFlow(Array.from(this.wizard.formStores.keys()));

    this.mutationInput = new Variables({
      initiativeData: () => ({
        title: this.initiative.title,
        slug: this.initiative.slug,
        description: '',
        organizationTypeId: this.initiative.organizationType,
        tags: this.tags.map(({ id, name, deleted }) => ({ id, name, deleted })),
        invites: this.invites.map(({ username }) => username),
      }),
      profileData: () => ({
        username: this.profile.user.username.trim(),
        bio: '',
        preferredName: '',
        email: this.profile.user.email.trim(),
        password: this.profile.user.password,
        userTypeId: this.profile.userType,
        emailsEnabled: true, // FIXME
        fullName: this.profile.fullName,
        locale: i18n.language,
        startFreeTrial: !!init.params.get('trial'),
      }),
    });

    this.signUpMutation = new SignUpOrganizationMutation(
      this.mutationInput,
      null,
      {},
      true,
    );
    this.signUpMutation.process(
      profile => ProfileModel.Load(profile),
      this.HandleNext,
      { subPath: 'profile' },
    );
  }

  @action HandleNext = profileModel => {
    if (!profileModel) {
      return;
    }
    clientStore.SetSignedInProfile(profileModel);
  };

  @action onSysError = () => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    // TODO: Distinguish case where not connected to internet / where there's a network error
    Alert.error(
      i18next.t(
        'Hmm... We failed to create your Organization. Please try again.',
      ),
    );
  };

  @action onUserErr = errs => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    this.wizard.ToErrorForm(errs);
    raiseUserError(errs, 'create', 'Profile');
  };

  @action onSuccess = () => {
    this.wizard.activeForm.SetPosting(false);
    this.wizard.clearDraft();
    clientStore.SetPosting(false);
    analytics.sendEvent('complete-signup-organization');
    if (this.mutationInput.profileData.startFreeTrial) {
      analytics.sendEvent('start-free-trial', {
        space: `+${this.mutationInput.initiativeData.slug}`,
      });
      analytics.sendEvent(
        'StartTrial',
        { value: 7, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
    }
    const {
      user: { username },
    } = this.profile;
    Alert.success(
      i18next.t('Welcome to {{siteName}}, {{username}}! 🙌', {
        siteName,
        username,
      }),
    );
    const { routerStore } = clientStore;
    analytics.sendEvent('complete-signup');
    analytics.sendEvent(
      'SubmitApplication',
      { value: 25, currency: 'EUR' },
      { twitter: true, facebook: true },
    );
    analytics.linkedinPixel('1169145');
    const { params } = this.manager;
    const next = params?.get('next');
    const billing = next && next === 'billing';
    if (billing) {
      analytics.sendEvent(
        'InitiateCheckout',
        { value: 5, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
      analytics.linkedinPixel('1169161');
    }
    routerStore.navigate('initiative', {
      initiative: `+${this.initiative.slug}`,
      subscribe: true,
    });
  };

  @action MakeItDouble = () => {
    if (!this.wizard.activeForm.isValid) {
      this.wizard.activeForm.alert();
      return;
    }
    this.wizard.activeForm.SetPosting(true);
    clientStore.SetPosting(true);
    const { onSysErr, onUserErr, onSuccess } = this;
    this.signUpMutation.setCallbacks({ onSysErr, onUserErr, onSuccess });
    this.signUpMutation.mutate();
  };
}
